/**
 * Coffee Making Minigame
 * 
 * A rhythm/timing game where you make drinks by hitting buttons in sequence.
 * The irony: you ARE the coffee machine, but you think you're operating one.
 */

export class CoffeeMakingGame {
  constructor(scene) {
    this.scene = scene;
    this.isActive = false;
    this.currentOrder = null;
    this.currentStep = 0;
    this.score = 0;
    this.combo = 0;
    
    // UI elements
    this.container = null;
    this.stepIndicators = [];
    this.timerBar = null;
    this.scoreText = null;
    
    // Timing
    this.stepTimer = null;
    this.timePerStep = 2000; // ms to complete each step
    this.timeRemaining = 0;
    
    // Drink recipes
    this.recipes = {
      espresso: {
        name: 'Espresso',
        steps: ['grind', 'tamp', 'brew'],
        difficulty: 1,
        basePrice: 3.50
      },
      americano: {
        name: 'Americano',
        steps: ['grind', 'tamp', 'brew', 'water'],
        difficulty: 1,
        basePrice: 4.00
      },
      latte: {
        name: 'Latte',
        steps: ['grind', 'tamp', 'brew', 'steam', 'pour'],
        difficulty: 2,
        basePrice: 5.50
      },
      cappuccino: {
        name: 'Cappuccino',
        steps: ['grind', 'tamp', 'brew', 'steam', 'foam', 'pour'],
        difficulty: 3,
        basePrice: 5.50
      },
      mocha: {
        name: 'Mocha',
        steps: ['grind', 'tamp', 'brew', 'chocolate', 'steam', 'pour', 'whip'],
        difficulty: 3,
        basePrice: 6.50
      },
      macchiato: {
        name: 'Caramel Macchiato',
        steps: ['grind', 'tamp', 'brew', 'steam', 'vanilla', 'pour', 'caramel'],
        difficulty: 4,
        basePrice: 6.00
      }
    };
    
    // Key mappings for steps
    this.stepKeys = {
      grind: 'G',
      tamp: 'T', 
      brew: 'B',
      water: 'W',
      steam: 'S',
      foam: 'F',
      pour: 'P',
      chocolate: 'C',
      whip: 'H',
      vanilla: 'V',
      caramel: 'A'
    };
    
    // Bind keyboard handler
    this.keyHandler = this.handleKeyPress.bind(this);
  }
  
  startOrder(drinkType, customizations = {}) {
    const recipe = this.recipes[drinkType];
    if (!recipe) {
      console.error('Unknown drink:', drinkType);
      return;
    }
    
    this.currentOrder = {
      type: drinkType,
      recipe: recipe,
      customizations: customizations,
      selectedIngredients: []
    };
    
    this.score = 0;
    this.isActive = true;
    
    // Hide portrait during coffee making
    if (window.dialogueManager && window.dialogueManager.portraitManager) {
      window.dialogueManager.portraitManager.hidePortrait();
    }
    
    this.createIngredientUI();
  }
  
  createIngredientUI() {
    const { width, height } = this.scene.scale;
    
    // Semi-transparent overlay
    this.overlay = this.scene.add.rectangle(
      width / 2, height / 2, width, height, 0x000000, 0.8
    );
    
    // Container for all minigame UI
    this.container = this.scene.add.container(width / 2, height / 2);
    
    // Title
    const title = this.scene.add.text(0, -220, `Making: ${this.currentOrder.recipe.name}`, {
      fontSize: '32px',
      fill: '#f4d03f',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(title);
    
    // Instruction
    const instruction = this.scene.add.text(0, -170, 'Select the correct ingredients:', {
      fontSize: '20px',
      fill: '#fff'
    }).setOrigin(0.5);
    this.container.add(instruction);
    
    // Show needed ingredients
    const neededText = this.scene.add.text(0, -130, 
      'Recipe: ' + this.currentOrder.recipe.steps.map(s => s.toUpperCase()).join(', '), {
      fontSize: '14px',
      fill: '#4CAF50',
      wordWrap: { width: 500 }
    }).setOrigin(0.5);
    this.container.add(neededText);
    
    // Available ingredients (includes correct ones + extras)
    const allIngredients = [
      'grind', 'tamp', 'brew', 'water', 'steam', 'foam', 
      'pour', 'chocolate', 'whip', 'vanilla', 'caramel', 'ice'
    ];
    
    // Shuffle for variety
    const shuffled = [...allIngredients].sort(() => Math.random() - 0.5);
    
    // Create ingredient buttons (3 rows of 4)
    const startY = -60;
    const buttonWidth = 120;
    const buttonHeight = 50;
    const spacing = 10;
    const columns = 4;
    
    this.ingredientButtons = [];
    
    shuffled.slice(0, 12).forEach((ingredient, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      const x = (col - 1.5) * (buttonWidth + spacing);
      const y = startY + row * (buttonHeight + spacing);
      
      const button = this.createIngredientButton(x, y, buttonWidth, buttonHeight, ingredient);
      this.ingredientButtons.push(button);
    });
    
    // Selected ingredients display
    this.selectedText = this.scene.add.text(0, 130, 'Selected: None', {
      fontSize: '16px',
      fill: '#fff'
    }).setOrigin(0.5);
    this.container.add(this.selectedText);
    
    // Done button
    this.createDoneButton();
  }
  
  createIngredientButton(x, y, w, h, ingredient) {
    const bg = this.scene.add.rectangle(x, y, w, h, 0x2C2C2C);
    bg.setStrokeStyle(2, 0x4CAF50);
    bg.setInteractive({ useHandCursor: true });
    
    const label = this.scene.add.text(x, y, ingredient.toUpperCase(), {
      fontSize: '14px',
      fill: '#fff'
    }).setOrigin(0.5);
    
    const button = { bg, label, ingredient, selected: false };
    
    bg.on('pointerdown', () => this.toggleIngredient(button));
    bg.on('pointerover', () => {
      if (!button.selected) bg.setFillStyle(0x3C3C3C);
    });
    bg.on('pointerout', () => {
      if (!button.selected) bg.setFillStyle(0x2C2C2C);
    });
    
    this.container.add([bg, label]);
    return button;
  }
  
  toggleIngredient(button) {
    button.selected = !button.selected;
    
    if (button.selected) {
      button.bg.setFillStyle(0x4CAF50);
      button.label.setColor('#000');
      this.currentOrder.selectedIngredients.push(button.ingredient);
    } else {
      button.bg.setFillStyle(0x2C2C2C);
      button.label.setColor('#fff');
      const idx = this.currentOrder.selectedIngredients.indexOf(button.ingredient);
      if (idx > -1) this.currentOrder.selectedIngredients.splice(idx, 1);
    }
    
    this.updateSelectedText();
  }
  
  updateSelectedText() {
    const selected = this.currentOrder.selectedIngredients;
    if (selected.length === 0) {
      this.selectedText.setText('Selected: None');
    } else {
      this.selectedText.setText('Selected: ' + selected.map(s => s.toUpperCase()).join(', '));
    }
  }
  
  createDoneButton() {
    const bg = this.scene.add.rectangle(0, 190, 200, 50, 0xf4d03f);
    bg.setInteractive({ useHandCursor: true });
    
    const label = this.scene.add.text(0, 190, 'DONE ✓', {
      fontSize: '20px',
      fill: '#1a1a2e',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    bg.on('pointerdown', () => this.checkOrder());
    bg.on('pointerover', () => bg.setFillStyle(0xffffff));
    bg.on('pointerout', () => bg.setFillStyle(0xf4d03f));
    
    this.container.add([bg, label]);
  }
  
  checkOrder() {
    // Compare selected ingredients with recipe
    const needed = this.currentOrder.recipe.steps;
    const selected = this.currentOrder.selectedIngredients;
    
    const correct = needed.filter(ing => selected.includes(ing)).length;
    const extra = selected.filter(ing => !needed.includes(ing)).length;
    const missing = needed.length - correct;
    
    // Calculate quality
    let qualityScore = 0;
    if (missing === 0 && extra === 0) {
      qualityScore = 100; // Perfect
    } else if (missing === 0 && extra <= 1) {
      qualityScore = 85; // Good (all correct, one extra)
    } else if (correct >= needed.length * 0.7 && extra <= 2) {
      qualityScore = 70; // OK (most correct, few extras)
    } else {
      qualityScore = 50; // Below average
    }
    
    this.completeOrder(qualityScore);
  }
  
  completeOrder(qualityScore) {
    this.isActive = false;
    
    // Calculate tip based on quality
    const basePrice = this.currentOrder.recipe.basePrice;
    let tipPercent = 0;
    let message = '';
    
    if (qualityScore >= 95) {
      tipPercent = 0.25;
      message = 'Perfect! The customer is thrilled!';
    } else if (qualityScore >= 75) {
      tipPercent = 0.15;
      message = 'Great job! The customer looks satisfied.';
    } else if (qualityScore >= 50) {
      tipPercent = 0.05;
      message = 'Not bad. The customer seems okay with it.';
    } else {
      tipPercent = 0;
      message = 'The customer looks disappointed...';
    }
    
    const tip = Math.round(basePrice * tipPercent * 100) / 100;
    
    // Clear current UI
    this.ingredientButtons.forEach(btn => {
      btn.bg.destroy();
      btn.label.destroy();
    });
    this.selectedText.destroy();
    this.container.removeAll(true);
    
    // Show results
    const resultText = this.scene.add.text(0, -50, [
      '☕',
      '',
      message,
      '',
      `Quality: ${qualityScore}%`,
      `Base: $${basePrice.toFixed(2)}`,
      `Tip: $${tip.toFixed(2)}`,
      `Total: $${(basePrice + tip).toFixed(2)}`
    ].join('\n'), {
      fontSize: '18px',
      fill: '#fff',
      align: 'center',
      lineSpacing: 8
    }).setOrigin(0.5);
    this.container.add(resultText);
    
    // Continue prompt
    const continueText = this.scene.add.text(0, 150, 'Press SPACE or tap to continue', {
      fontSize: '16px',
      fill: '#888'
    }).setOrigin(0.5);
    this.container.add(continueText);
    
    // Flash continue text
    this.scene.tweens.add({
      targets: continueText,
      alpha: 0.3,
      yoyo: true,
      repeat: -1,
      duration: 500
    });
    
    // Store result
    this.result = {
      drinkType: this.currentOrder.type,
      qualityScore,
      basePrice,
      tip,
      total: basePrice + tip,
      score: qualityScore
    };
    
    // Wait for space or click to close
    const closeHandler = (event) => {
      if (event.code === 'Space' || event.type === 'pointerdown') {
        document.removeEventListener('keydown', closeHandler);
        this.overlay.removeInteractive();
        this.cleanup();
        
        // Emit completion event
        if (this.onComplete) {
          this.onComplete(this.result);
        }
      }
    };
    document.addEventListener('keydown', closeHandler);
    this.overlay.setInteractive();
    this.overlay.on('pointerdown', closeHandler);
  }
  
  createUI_OLD() {
    const { width, height } = this.scene.scale;
    
    // Semi-transparent overlay
    this.overlay = this.scene.add.rectangle(
      width / 2, height / 2, width, height, 0x000000, 0.8
    );
    
    // Container for all minigame UI
    this.container = this.scene.add.container(width / 2, height / 2);
    
    // Title
    const title = this.scene.add.text(0, -200, `Making: ${this.currentOrder.recipe.name}`, {
      fontSize: '32px',
      fill: '#f4d03f',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(title);
    
    // Step indicators
    const steps = this.currentOrder.recipe.steps;
    const stepWidth = 80;
    const totalWidth = steps.length * stepWidth;
    const startX = -totalWidth / 2 + stepWidth / 2;
    
    this.stepIndicators = [];
    steps.forEach((step, i) => {
      const x = startX + i * stepWidth;
      
      // Step circle
      const circle = this.scene.add.circle(x, -100, 30, 0x333333);
      circle.setStrokeStyle(3, 0x666666);
      
      // Step letter
      const letter = this.scene.add.text(x, -100, this.stepKeys[step], {
        fontSize: '24px',
        fill: '#888'
      }).setOrigin(0.5);
      
      // Step name below
      const name = this.scene.add.text(x, -55, step.toUpperCase(), {
        fontSize: '10px',
        fill: '#666'
      }).setOrigin(0.5);
      
      this.stepIndicators.push({ circle, letter, name, step });
      this.container.add([circle, letter, name]);
    });
    
    // Current step highlight
    this.currentStepHighlight = this.scene.add.text(0, 0, '', {
      fontSize: '64px',
      fill: '#fff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(this.currentStepHighlight);
    
    // Instruction
    this.instructionText = this.scene.add.text(0, 60, '', {
      fontSize: '20px',
      fill: '#aaa'
    }).setOrigin(0.5);
    this.container.add(this.instructionText);
    
    // Timer bar
    this.timerBarBg = this.scene.add.rectangle(0, 120, 300, 20, 0x333333);
    this.timerBar = this.scene.add.rectangle(-150, 120, 300, 20, 0x4CAF50);
    this.timerBar.setOrigin(0, 0.5);
    this.container.add([this.timerBarBg, this.timerBar]);
    
    // Score
    this.scoreText = this.scene.add.text(0, 170, 'Score: 0', {
      fontSize: '24px',
      fill: '#fff'
    }).setOrigin(0.5);
    this.container.add(this.scoreText);
    
    // Combo
    this.comboText = this.scene.add.text(0, 200, '', {
      fontSize: '18px',
      fill: '#f4d03f'
    }).setOrigin(0.5);
    this.container.add(this.comboText);
  }
  
  startStep() {
    if (this.currentStep >= this.currentOrder.recipe.steps.length) {
      this.completeOrder();
      return;
    }
    
    const step = this.currentOrder.recipe.steps[this.currentStep];
    const key = this.stepKeys[step];
    
    // Update current step display
    this.currentStepHighlight.setText(`Press ${key}`);
    this.instructionText.setText(`${step.toUpperCase()}`);
    
    // Highlight current step indicator
    const indicator = this.stepIndicators[this.currentStep];
    indicator.circle.setStrokeStyle(4, 0xf4d03f);
    indicator.letter.setColor('#f4d03f');
    
    // Start timer
    this.timeRemaining = this.timePerStep;
    this.timerBar.width = 300;
    this.timerBar.setFillStyle(0x4CAF50);
    
    // Animate timer
    if (this.timerTween) this.timerTween.stop();
    this.timerTween = this.scene.tweens.add({
      targets: this.timerBar,
      width: 0,
      duration: this.timePerStep,
      ease: 'Linear',
      onUpdate: () => {
        const progress = this.timerBar.width / 300;
        if (progress < 0.3) {
          this.timerBar.setFillStyle(0xf44336); // Red
        } else if (progress < 0.6) {
          this.timerBar.setFillStyle(0xFFC107); // Yellow
        }
      },
      onComplete: () => {
        this.recordResult('miss');
        this.nextStep();
      }
    });
  }
  
  handleKeyPress(event) {
    if (!this.isActive) return;
    
    const step = this.currentOrder.recipe.steps[this.currentStep];
    const expectedKey = this.stepKeys[step].toLowerCase();
    const pressedKey = event.key.toLowerCase();
    
    if (pressedKey === expectedKey) {
      // Correct key! Calculate timing score
      const progress = this.timerBar.width / 300;
      let result;
      
      if (progress > 0.7) {
        result = 'perfect';
        this.showFeedback('PERFECT!', '#4CAF50');
      } else if (progress > 0.4) {
        result = 'good';
        this.showFeedback('Good!', '#8BC34A');
      } else if (progress > 0.2) {
        result = 'ok';
        this.showFeedback('OK', '#FFC107');
      } else {
        result = 'late';
        this.showFeedback('Late...', '#FF9800');
      }
      
      this.recordResult(result);
      this.nextStep();
    } else {
      // Wrong key
      this.showFeedback('Wrong key!', '#f44336');
    }
  }
  
  recordResult(result) {
    this.currentOrder.stepResults.push(result);
    
    // Update score
    const points = {
      perfect: 100,
      good: 75,
      ok: 50,
      late: 25,
      miss: 0
    };
    
    const stepScore = points[result] || 0;
    
    // Combo bonus
    if (result === 'perfect' || result === 'good') {
      this.combo++;
      const comboBonus = Math.min(this.combo * 10, 50);
      this.score += stepScore + comboBonus;
      this.comboText.setText(`Combo x${this.combo}!`);
    } else {
      this.combo = 0;
      this.score += stepScore;
      this.comboText.setText('');
    }
    
    this.scoreText.setText(`Score: ${this.score}`);
    
    // Update step indicator
    const indicator = this.stepIndicators[this.currentStep];
    const colors = {
      perfect: 0x4CAF50,
      good: 0x8BC34A,
      ok: 0xFFC107,
      late: 0xFF9800,
      miss: 0xf44336
    };
    indicator.circle.setFillStyle(colors[result]);
    indicator.circle.setStrokeStyle(3, colors[result]);
    indicator.letter.setColor('#fff');
  }
  
  showFeedback(text, color) {
    const feedback = this.scene.add.text(0, -30, text, {
      fontSize: '28px',
      fill: color,
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.container.add(feedback);
    
    this.scene.tweens.add({
      targets: feedback,
      y: -60,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => feedback.destroy()
    });
  }
  
  nextStep() {
    if (this.timerTween) this.timerTween.stop();
    this.currentStep++;
    
    // Brief pause before next step
    this.scene.time.delayedCall(300, () => {
      this.startStep();
    });
  }
  
  completeOrder() {
    this.isActive = false;
    document.removeEventListener('keydown', this.keyHandler);
    
    // Calculate final quality
    const results = this.currentOrder.stepResults;
    const perfectCount = results.filter(r => r === 'perfect').length;
    const goodCount = results.filter(r => r === 'good').length;
    const missCount = results.filter(r => r === 'miss').length;
    
    const totalSteps = results.length;
    const qualityScore = Math.round(
      ((perfectCount * 100 + goodCount * 75 + (totalSteps - perfectCount - goodCount - missCount) * 50) / totalSteps)
    );
    
    // Calculate tip based on quality
    const basePrice = this.currentOrder.recipe.basePrice;
    let tipPercent = 0;
    let message = '';
    
    if (qualityScore >= 90) {
      tipPercent = 0.25;
      message = 'Perfect brew! The customer is thrilled!';
    } else if (qualityScore >= 75) {
      tipPercent = 0.15;
      message = 'Great job! The customer looks satisfied.';
    } else if (qualityScore >= 50) {
      tipPercent = 0.05;
      message = 'Not bad. The customer seems okay with it.';
    } else {
      tipPercent = 0;
      message = 'The customer looks disappointed...';
    }
    
    const tip = Math.round(basePrice * tipPercent * 100) / 100;
    
    // Show results
    this.currentStepHighlight.setText('☕');
    this.instructionText.setText(message);
    
    const resultText = this.scene.add.text(0, -30, [
      `Quality: ${qualityScore}%`,
      `Base: $${basePrice.toFixed(2)}`,
      `Tip: $${tip.toFixed(2)}`,
      `Total: $${(basePrice + tip).toFixed(2)}`
    ].join('\n'), {
      fontSize: '18px',
      fill: '#fff',
      align: 'center'
    }).setOrigin(0.5);
    this.container.add(resultText);
    
    // Continue prompt
    const continueText = this.scene.add.text(0, 230, 'Press SPACE to continue', {
      fontSize: '16px',
      fill: '#888'
    }).setOrigin(0.5);
    this.container.add(continueText);
    
    // Flash continue text
    this.scene.tweens.add({
      targets: continueText,
      alpha: 0.3,
      yoyo: true,
      repeat: -1,
      duration: 500
    });
    
    // Store result
    this.result = {
      drinkType: this.currentOrder.type,
      qualityScore,
      basePrice,
      tip,
      total: basePrice + tip,
      score: this.score
    };
    
    // Wait for space to close
    const closeHandler = (event) => {
      if (event.code === 'Space') {
        document.removeEventListener('keydown', closeHandler);
        this.cleanup();
        
        // Emit completion event
        if (this.onComplete) {
          this.onComplete(this.result);
        }
      }
    };
    document.addEventListener('keydown', closeHandler);
  }
  
  cleanup() {
    if (this.overlay) this.overlay.destroy();
    if (this.container) this.container.destroy();
    if (this.timerTween) this.timerTween.stop();
    this.isActive = false;
    
    // Restore portrait if NPC is still in conversation
    if (window.dialogueManager && window.dialogueManager.currentNpc && window.dialogueManager.portraitManager) {
      window.dialogueManager.portraitManager.showPortrait(
        window.dialogueManager.currentNpc, 
        window.dialogueManager.currentExpression || 'neutral'
      );
    }
  }
  
  // Get a random drink order based on game progression
  static getRandomOrder(gameDay) {
    const drinks = Object.keys(CoffeeMakingGame.prototype.recipes);
    
    // Early game: simple drinks
    // Later: more complex drinks
    let availableDrinks;
    if (gameDay < 5) {
      availableDrinks = ['espresso', 'americano'];
    } else if (gameDay < 15) {
      availableDrinks = ['espresso', 'americano', 'latte', 'cappuccino'];
    } else {
      availableDrinks = drinks;
    }
    
    return availableDrinks[Math.floor(Math.random() * availableDrinks.length)];
  }
}
